import { useEffect, useState } from "react";
import { ClearBadge, Color } from "@bonliva-crm/ui";
import locale from "@bonliva-crm/locale";
import { Box, Slider, Typography, makeStyles } from "@material-ui/core";
import { useFormField } from "@clientUtils";
import { FilterWithFavouriteProps } from "../utils/filters-record-types";
import { CollapsibleFavFilter } from "./_base/collapsible-fav-filter";

export type WageTypeFilterData = {
  from: number | null;
  to: number | null;
};

export type WagesTypeFilterProps = FilterWithFavouriteProps;

export const WagesTypeFilter = (props: WagesTypeFilterProps) => {
  const classes = useStyles();
  const title = props.label ?? locale.WagesType;
  const { field, fieldState, setFieldValue } = useFormField<WageTypeFilterData | null>(
    props.name
  );

  const [value, setValue] = useState([
    field?.value?.from ?? 0,
    field?.value?.to ?? 3000
  ] as [number, number]);

  useEffect(() => {
    setValue([field?.value?.from ?? 0, field?.value?.to ?? 3000]);
  }, [field?.value?.from, field?.value?.to]);

  const handleFinalChange = (newValue: number[]) => {
    const from = newValue[0] === 0 ? null : newValue[0]; // reset to form defaults
    const to = newValue[1] === 3000 ? null : newValue[1]; // reset to form defaults

    setFieldValue({ from, to }, { shouldDirty: true });
  };

  const clear = () => {
    setFieldValue({ from: null, to: null }, { shouldDirty: true });
  };

  if (props.setFavorite) {
    return (
      <CollapsibleFavFilter
        title={title}
        favorite={props.favorite}
        onSetFavorite={props.setFavorite}
        isClearable={fieldState.isDirty}
        onClear={clear}
        isDirty={fieldState.isDirty}
        initialExpanded={fieldState.isDirty}
      >
        <Box pr={2} pt={2}>
          <Slider
            value={[value[0], value[1]]}
            onChange={(_, newValue) => setValue(newValue as [number, number])}
            onChangeCommitted={(_, newValue) => handleFinalChange(newValue as number[])}
            valueLabelDisplay="on"
            min={0}
            max={3000}
            step={250}
            marks={[
              { value: 0, label: "0 kr" },
              { value: 3000, label: "3000 kr" }
            ]}
            aria-labelledby="discrete-slider"
            classes={{
              markLabel: classes.sliderMarkLabel
            }}
          />
        </Box>
      </CollapsibleFavFilter>
    );
  }

  return (
    <ClearBadge onClear={clear}>
      <div
        style={{
          border: `1px solid ${Color.LightGray}`,
          borderRadius: "4px",
          padding: "8px",
          paddingLeft: 16,
          paddingRight: 16,
          paddingTop: 16,
          minWidth: "300px"
        }}
      >
        <Typography variant="body1" style={{ color: Color.DetailsInput }}>
          {title}
        </Typography>

        <Box pl="10px" pr={1} pt={2}>
          <Slider
            value={[value[0], value[1]]}
            onChange={(_, newValue) => setValue(newValue as [number, number])}
            onChangeCommitted={(_, newValue) => handleFinalChange(newValue as number[])}
            valueLabelDisplay="on"
            valueLabelFormat={(v) => <span style={{ whiteSpace: "nowrap" }}>{v} kr</span>}
            min={0}
            max={3000}
            step={250}
            marks={[{ value: 0 }, { value: 3000 }]}
            aria-labelledby="discrete-slider"
            classes={{
              thumb: classes.miniSliderThumb
            }}
          />
        </Box>
      </div>
    </ClearBadge>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gap: theme.spacing(2)
  },

  sliderMarkLabel: {
    "&[data-index='1']": {
      transform: "translateX(-75%)"
    }
  },

  miniSliderThumb: {
    height: 16,
    width: 16,
    borderWidth: 3,
    marginTop: -7,

    "& > span:first-child": {
      transform: "scale(1) translateY(-6px) translateX(-5px) !important"
    }
  }
}));
